import type { FC, ReactNode } from 'react'

import {
    EyeOff,
    Gauge,
    Hand,
    Headset,
    Power,
    ShieldCheck
} from 'lucide-react'
import Reveal from '@/components/generals/reveal'

const SAFETY_POINTS = [
    {
        icon: ShieldCheck,
        title: "Yields on contact",
        body: "Force-limited joints soften the instant they meet unexpected resistance, and stop within 50 ms."
    },
    {
        icon: Gauge,
        title: "Walks at a human pace",
        body: "Speed is capped indoors, and lower still near children, pets and stairs."
    },
    {
        icon: EyeOff,
        title: "Private by design",
        body: "Vision and voice are processed on board. A physical shutter closes the cameras whenever you like."
    },
    {
        icon: Hand,
        title: "Asks before it acts",
        body: "Unfamiliar objects, closed doors and new chores all get a question first."
    },
    {
        icon: Power,
        title: "Stops on a word",
        body: "Say “Oriel, stop”, press the button between its shoulders, or tap stop in the app."
    },
    {
        icon: Headset,
        title: "Expert help, only with you",
        body: "For a job it hasn’t learned, a trained specialist can guide it remotely, only in a session you approve."
    }
]

const Safety: FC = (): ReactNode => {
    return (
        <section id='safety' aria-labelledby='safety-title' className='bg-sand'>
            <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-28'>
                <div className='grid gap-6 lg:grid-cols-12 lg:gap-10'>
                    <Reveal className='lg:col-span-7'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember-deep'>
                            Safety and privacy
                        </p>

                        <h2 id='safety-title' className='mt-5 font-heading text-[clamp(2.6rem,5.4vw,5rem)] leading-[0.96] tracking-[-0.02em]'>
                            Soft, slow, and <em className='italic text-ember-deep'>always listening for stop.</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={150} className='flex items-end lg:col-span-5'>
                        <p className='max-w-md text-lg leading-relaxed text-muted-foreground'>
                            A robot that shares your hallway has to be gentle before it is clever. These limits are built into the hardware, not added as settings.
                        </p>
                    </Reveal>
                </div>

                <ul className='mt-12 grid gap-px overflow-hidden rounded-[2rem] border border-foreground/10 bg-foreground/10 sm:grid-cols-2 md:mt-16 lg:grid-cols-3'>
                    {SAFETY_POINTS.map((point, index) => (
                        <li key={point.title} className='bg-sand'>
                            <Reveal delay={index * 70} className='flex h-full flex-col gap-4 p-7 transition-colors duration-500 hover:bg-background/60 md:p-9'>
                                <span className='flex size-11 items-center justify-center rounded-full bg-foreground text-background'>
                                    <point.icon className='size-5' aria-hidden='true' />
                                </span>

                                <h3 className='mt-2 font-heading text-[1.9rem] leading-tight'>
                                    {point.title}
                                </h3>

                                <p className='max-w-sm text-[15px] leading-relaxed text-muted-foreground'>
                                    {point.body}
                                </p>
                            </Reveal>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export default Safety
